import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WompiRelayService } from '../../../../services/wompi-relay.service';
import { LoadingService } from '../../../../services/loading.service';
import { ToastService } from '../../../../services/toast.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface RelayLog {
  id: number;
  relayUrlId: number;
  relayUrlName: string;
  targetUrl: string;
  webhookRequestId?: number;
  requestSentAt: Date | string;
  responseReceivedAt?: Date | string;
  httpStatusCode?: number;
  success: boolean;
  attemptNumber: number;
  requestBody: string;
  requestHeaders: string;
  responseBody?: string;
  responseHeaders?: string;
  errorMessage?: string;
  durationMs: number;
  wompiTransactionId?: string;
  wompiPaymentLinkId?: string;
}

interface PagedResult {
  data: RelayLog[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  firstItemOnPage: number;
  lastItemOnPage: number;
}

@Component({
  selector: 'app-wompi-relay-logs',
  templateUrl: './wompi-relay-logs.component.html',
  styleUrls: ['./wompi-relay-logs.component.scss']
})
export class WompiRelayLogsComponent implements OnInit {
  logs: RelayLog[] = [];
  relayUrlId: number | null = null;
  relayUrlName: string = '';
  expandedLogId: number | null = null;
  loading: boolean = false;

  // Filtros
  successFilter: boolean | null = null;
  fromDate: string = '';
  toDate: string = '';
  searchTerm: string = '';

  // Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalCount: number = 0;
  totalPages: number = 0;
  hasPreviousPage: boolean = false;
  hasNextPage: boolean = false;
  firstItemOnPage: number = 0;
  lastItemOnPage: number = 0;

  // Debounce para búsqueda
  private searchSubject = new Subject<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private wompiRelayService: WompiRelayService,
    private loadingService: LoadingService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.relayUrlId = Number(this.route.snapshot.paramMap.get('id'));

    // Debounce de búsqueda (500ms)
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadLogs();
    });

    if (this.relayUrlId) {
      this.loadLogs();
    } else {
      this.toastService.showToast('error', 'ID de URL de relay no válido');
      this.router.navigate(['/dashboard/config/wompi-relay']);
    }
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  private buildFilter(): any {
    const filter: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    };

    if (this.successFilter !== null && this.successFilter !== undefined) {
      filter.success = this.successFilter;
    }
    if (this.fromDate) {
      filter.fromDate = new Date(this.fromDate).toISOString();
    }
    if (this.toDate) {
      filter.toDate = new Date(this.toDate).toISOString();
    }
    if (this.searchTerm && this.searchTerm.trim()) {
      filter.searchTerm = this.searchTerm.trim();
    }

    return filter;
  }

  private loadLogs(): void {
    this.loading = true;
    const filter = this.buildFilter();

    this.wompiRelayService.getLogsByUrlId(this.relayUrlId!, filter).subscribe({
      next: (response) => {
        // El backend devuelve PagedResult directamente
        if (response && response.data) {
          this.logs = response.data;
          this.totalCount = response.totalCount;
          this.totalPages = response.totalPages;
          this.pageNumber = response.pageNumber;
          this.pageSize = response.pageSize;
          this.hasPreviousPage = response.hasPreviousPage;
          this.hasNextPage = response.hasNextPage;
          this.firstItemOnPage = response.firstItemOnPage;
          this.lastItemOnPage = response.lastItemOnPage;
        } else if (Array.isArray(response)) {
          // Fallback por si devuelve array directo
          this.logs = response;
          this.totalCount = response.length;
          this.totalPages = 1;
        } else {
          this.logs = [];
          this.totalCount = 0;
          this.totalPages = 0;
        }

        if (this.logs.length > 0 && !this.relayUrlName) {
          this.relayUrlName = this.logs[0].relayUrlName;
        }
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.toastService.showToast('error', 'Error al cargar los logs');
        this.loading = false;
      }
    });
  }

  // Paginación
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.pageNumber = page;
    this.loadLogs();
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.pageNumber++;
      this.loadLogs();
    }
  }

  previousPage(): void {
    if (this.hasPreviousPage) {
      this.pageNumber--;
      this.loadLogs();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.pageNumber - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  toggleLogDetails(logId: number): void {
    this.expandedLogId = this.expandedLogId === logId ? null : logId;
  }

  isExpanded(logId: number): boolean {
    return this.expandedLogId === logId;
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleString('es-ES');
  }

  formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  }

  getStatusClass(statusCode?: number): string {
    if (!statusCode) return 'bg-gray-100 text-gray-800';
    if (statusCode >= 200 && statusCode < 300) return 'bg-green-100 text-green-800';
    if (statusCode >= 300 && statusCode < 400) return 'bg-blue-100 text-blue-800';
    if (statusCode >= 400 && statusCode < 500) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  }

  prettyPrintJson(jsonString: string): string {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.loadLogs();
  }

  clearFilters(): void {
    this.successFilter = null;
    this.fromDate = '';
    this.toDate = '';
    this.searchTerm = '';
    this.pageNumber = 1;
    this.loadLogs();
  }

  goBack(): void {
    this.router.navigate(['/dashboard/config/wompi-relay']);
  }
}
